// src/services/statusService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

class StatusService {
  constructor() {
    this.booksCollection = 'pb-books';
  }

  // ============ ADD STATUS UPDATE ============
  
  async addStatusUpdate(bookId, galleryId, itemId, statusData) {
    try {
      const updatesRef = collection(
        db, 
        this.booksCollection, 
        bookId, 
        'galleries', 
        galleryId, 
        'status-updates'
      );

      const updateDoc = {
        itemId: itemId,
        itemType: statusData.itemType || 'member', // member, customer, product, asset, etc.
        statusType: statusData.statusType, // wellbeing, attendance, inventory, delivery, etc.
        statusId: statusData.statusId,
        statusLabel: statusData.statusLabel,
        statusIcon: statusData.statusIcon || null,
        statusColor: statusData.statusColor || '#6b7280',
        notes: statusData.notes || '',
        recordedBy: statusData.recordedBy,
        recordedByEmail: statusData.recordedByEmail || null,
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0],
        metadata: statusData.metadata || {}
      };

      const docRef = await addDoc(updatesRef, updateDoc);

      // Update item's current status
      await this.updateItemCurrentStatus(bookId, galleryId, itemId, statusData);

      return { id: docRef.id, ...updateDoc };
    } catch (error) {
      console.error('Error adding status update:', error);
      throw error;
    }
  }

  // ============ UPDATE ITEM CURRENT STATUS ============
  
  async updateItemCurrentStatus(bookId, galleryId, itemId, statusData) {
    try {
      const itemRef = doc(
        db, 
        this.booksCollection, 
        bookId, 
        'galleries', 
        galleryId, 
        'media', 
        itemId
      );
      
      const currentStatus = {
        [`currentStatus_${statusData.statusType}`]: {
          statusId: statusData.statusId,
          statusLabel: statusData.statusLabel,
          statusIcon: statusData.statusIcon || null,
          statusColor: statusData.statusColor || '#6b7280',
          lastUpdated: new Date().toISOString(),
          updatedBy: statusData.recordedBy
        }
      };

      await updateDoc(itemRef, currentStatus);
    } catch (error) {
      console.error('Error updating item current status:', error);
      // Don't throw - status history is more important than current status field
    }
  }

  // ============ GET STATUS HISTORY ============
  
  async getStatusHistory(bookId, galleryId, itemId = null, filters = {}) {
    try {
      let updatesQuery = query(
        collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'status-updates'),
        orderBy('timestamp', 'desc')
      );

      // Filter by specific item
      if (itemId) {
        updatesQuery = query(
          collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'status-updates'),
          where('itemId', '==', itemId),
          orderBy('timestamp', 'desc')
        );
      }

      // Filter by item type
      if (filters.itemType) {
        updatesQuery = query(updatesQuery, where('itemType', '==', filters.itemType));
      }

      // Filter by status type
      if (filters.statusType) {
        updatesQuery = query(updatesQuery, where('statusType', '==', filters.statusType));
      }

      // Limit results
      if (filters.limit) {
        updatesQuery = query(updatesQuery, limit(filters.limit));
      }

      const snapshot = await getDocs(updatesQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting status history:', error);
      return [];
    }
  }

  // ============ GET LATEST STATUS ============
  
  async getLatestStatus(bookId, galleryId, itemId, statusType) {
    try {
      const latestQuery = query(
        collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'status-updates'),
        where('itemId', '==', itemId),
        where('statusType', '==', statusType),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(latestQuery);
      if (snapshot.empty) return null;
      
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
      console.error('Error getting latest status:', error);
      return null;
    }
  }

  // ============ BULK OPERATIONS ============
  
  async bulkAddStatus(bookId, galleryId, itemIds, statusData) {
    try {
      const promises = itemIds.map(itemId => 
        this.addStatusUpdate(bookId, galleryId, itemId, {
          ...statusData,
          metadata: { ...statusData.metadata, bulkUpdate: true }
        })
      );

      const results = await Promise.allSettled(promises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { success: true, successful, failed, total: itemIds.length };
    } catch (error) {
      console.error('Error in bulk status update:', error);
      throw error;
    }
  }

  // ============ ANALYTICS ============
  
  async getStatusAnalytics(bookId, galleryId, filters = {}) {
    try {
      const dateRange = filters.dateRange || 7;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);
      const startDateStr = startDate.toISOString().split('T')[0];

      let analyticsQuery = query(
        collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'status-updates'),
        where('date', '>=', startDateStr),
        orderBy('date', 'desc')
      );

      // Apply filters
      if (filters.itemType) {
        analyticsQuery = query(analyticsQuery, where('itemType', '==', filters.itemType));
      }

      if (filters.statusType) {
        analyticsQuery = query(analyticsQuery, where('statusType', '==', filters.statusType));
      }

      const snapshot = await getDocs(analyticsQuery);
      const updates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate analytics
      const analytics = {
        totalUpdates: updates.length,
        uniqueItems: new Set(updates.map(u => u.itemId)).size,
        statusDistribution: {},
        itemTypeDistribution: {},
        dailyTrends: {},
        topUpdaters: {},
        recentUpdates: updates.slice(0, 10)
      };

      updates.forEach(update => {
        // Status distribution
        const statusKey = `${update.statusType}:${update.statusId}`;
        analytics.statusDistribution[statusKey] = 
          (analytics.statusDistribution[statusKey] || 0) + 1;

        // Item type distribution
        analytics.itemTypeDistribution[update.itemType] = 
          (analytics.itemTypeDistribution[update.itemType] || 0) + 1;

        // Daily trends
        analytics.dailyTrends[update.date] = 
          (analytics.dailyTrends[update.date] || 0) + 1;

        // Top updaters
        if (update.recordedBy) {
          analytics.topUpdaters[update.recordedBy] = 
            (analytics.topUpdaters[update.recordedBy] || 0) + 1;
        }
      });

      return analytics;
    } catch (error) {
      console.error('Error getting status analytics:', error);
      throw error;
    }
  }

  // ============ ALERTS & MONITORING ============
  
  async checkStatusAlerts(bookId, galleryId, itemId, statusId, alertConfig) {
    try {
      if (!alertConfig.threshold) return null;

      const { threshold, period, severity } = alertConfig;
      const days = period === 'week' ? 7 : period === 'month' ? 30 : period === 'day' ? 1 : 7;

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const startDateStr = startDate.toISOString().split('T')[0];

      const alertQuery = query(
        collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'status-updates'),
        where('itemId', '==', itemId),
        where('statusId', '==', statusId),
        where('date', '>=', startDateStr)
      );

      const snapshot = await getDocs(alertQuery);
      const count = snapshot.size;

      if (count >= threshold) {
        return {
          alert: true,
          itemId,
          statusId,
          count,
          threshold,
          period,
          severity: severity || (count >= threshold * 2 ? 'high' : 'medium'),
          triggeredAt: new Date().toISOString()
        };
      }

      return null;
    } catch (error) {
      console.error('Error checking status alerts:', error);
      return null;
    }
  }

  // ============ BATCH UPDATES ============
  
  async batchUpdateStatuses(bookId, galleryId, updates) {
    try {
      const batch = writeBatch(db);
      const updatesRef = collection(
        db, 
        this.booksCollection, 
        bookId, 
        'galleries', 
        galleryId, 
        'status-updates'
      );

      updates.forEach(update => {
        const docRef = doc(updatesRef);
        batch.set(docRef, {
          ...update,
          timestamp: serverTimestamp(),
          date: new Date().toISOString().split('T')[0]
        });
      });

      await batch.commit();
      return { success: true, count: updates.length };
    } catch (error) {
      console.error('Error in batch update:', error);
      throw error;
    }
  }

  // ============ DELETE/ARCHIVE ============
  
  async deleteStatusUpdate(bookId, galleryId, updateId) {
    try {
      const updateRef = doc(
        db, 
        this.booksCollection, 
        bookId, 
        'galleries', 
        galleryId, 
        'status-updates',
        updateId
      );

      await deleteDoc(updateRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting status update:', error);
      throw error;
    }
  }

  async archiveOldUpdates(bookId, galleryId, daysToKeep = 90) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      const cutoffDateStr = cutoffDate.toISOString().split('T')[0];

      const oldUpdatesQuery = query(
        collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'status-updates'),
        where('date', '<', cutoffDateStr)
      );

      const snapshot = await getDocs(oldUpdatesQuery);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => batch.delete(doc.ref));
      
      await batch.commit();
      return { success: true, deletedCount: snapshot.size };
    } catch (error) {
      console.error('Error archiving old updates:', error);
      throw error;
    }
  }

  // ============ EXPORT ============
  
  async exportStatusHistory(bookId, galleryId, filters = {}) {
    try {
      const history = await this.getStatusHistory(bookId, galleryId, null, filters);
      
      return {
        exportDate: new Date().toISOString(),
        bookId,
        galleryId,
        filters,
        totalRecords: history.length,
        data: history
      };
    } catch (error) {
      console.error('Error exporting status history:', error);
      throw error;
    }
  }
}

export const statusService = new StatusService();